"use client";

// The compound root: `<RelayDeposit>` + `.Trigger` + `.Content`, assembling
// Tasks 3-10 into the user-facing deposit modal. This file owns wiring plus one
// piece of state: the modal's OPEN flag — every prop goes into
// `RelayDepositContext` (so `.Content`, supplied by the host as a child, can
// read them) and the shell knobs go into `ResponsiveShell` (radix Dialog on
// desktop, vaul Drawer on mobile).
//
// Why `open` lives here rather than in `ResponsiveShell`: the wizard's state
// host must be mounted for exactly as long as the modal is open, and it sits
// ABOVE the shell (see RelayWizardStateHost.tsx) — so the flag has to be known
// above the shell too. `ResponsiveShell` keeps its own controlled/uncontrolled
// handling for other callers (e.g. `<RelayHistory>`, which has no wizard state
// to preserve and stays a plain pass-through); this root simply always drives
// it as fully controlled.
//
// Compound shape mirrors `WalletConnectModal`
// (`packages/dapp-ui/src/features/wallet-connect-modal/components/WalletConnectModal.tsx`):
// an `Object.assign` on the root function so `<RelayDeposit.Trigger>` /
// `<RelayDeposit.Content>` are reachable without extra imports.
import * as React from "react";

import { RelayDepositContext } from "../providers/relay-deposit-provider";
import type { RelayDepositProps } from "../types";
import { RelayDepositContent } from "./RelayDepositContent";
import { RelayDepositTrigger } from "./RelayDepositTrigger";
import { RelayWizardStateHost } from "./RelayWizardStateHost";
import { ResponsiveShell } from "./shell/ResponsiveShell";
import "../styles/component.css";

function RelayDepositRoot(props: RelayDepositProps) {
  const {
    theme = "dark",
    mobileBreakpoint,
    drawerDirection,
    dialogWidth,
    drawerMaxWidth,
    drawerMinWidth,
    style,
    open: openProp,
    onOpenChange,
    children,
  } = props;

  // Same controlled/uncontrolled contract as before, just resolved one level
  // higher: an `open` prop wins outright, and `onOpenChange` is notified either
  // way (moved verbatim out of ResponsiveShell).
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const handleOpenChange = React.useCallback(
    (value: boolean) => {
      if (!isControlled) setInternalOpen(value);
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange],
  );

  return (
    // `props` is a fresh object each render, so consumers re-render exactly when
    // this root does -- i.e. the same cadence they'd get from prop drilling.
    // Memoizing it would only pretend to be stable (every field is a prop).
    <RelayDepositContext.Provider value={props}>
      <RelayWizardStateHost open={open}>
        <ResponsiveShell
          theme={theme}
          mobileBreakpoint={mobileBreakpoint}
          drawerDirection={drawerDirection}
          dialogWidth={dialogWidth}
          drawerMaxWidth={drawerMaxWidth}
          drawerMinWidth={drawerMinWidth}
          style={style}
          open={open}
          onOpenChange={handleOpenChange}
        >
          {children}
        </ResponsiveShell>
      </RelayWizardStateHost>
    </RelayDepositContext.Provider>
  );
}

export const RelayDeposit = Object.assign(RelayDepositRoot, {
  Trigger: RelayDepositTrigger,
  Content: RelayDepositContent,
});
